import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage, logAdminActivity } from '../../lib/firebase';
import { Trash2, Edit, Check, X, TreePine, Filter, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import EditTreeModal from './EditTreeModal';
import ImageWithFallback from '../ImageWithFallback';

interface Tree {
  id: string;
  name: string;
  creator: string;
  email: string;
  imageUrl: string;
  createdAt: Date;
  status: 'pending' | 'approved' | 'rejected' | 'deleted';
  votes: number;
}

export default function TreesList() {
  const [trees, setTrees] = useState<Tree[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTree, setEditingTree] = useState<Tree | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'deleted'>('all');

  const loadTrees = async () => {
    try {
      const treesQuery = query(
        collection(db, 'trees'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(treesQuery);
      const treeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Tree[];
      setTrees(treeData);
    } catch (error) {
      console.error('Error loading trees:', error);
      toast.error('Error al cargar los árboles');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTrees();
  }, []);

  const handleStatusChange = async (tree: Tree, newStatus: Tree['status']) => {
    try {
      const treeRef = doc(db, 'trees', tree.id);
      await updateDoc(treeRef, { status: newStatus });
      
      await logAdminActivity(newStatus === 'approved' ? 'approve' : 'reject', {
        treeId: tree.id,
        treeName: tree.name,
        previousStatus: tree.status,
        newStatus: newStatus
      });
      
      await loadTrees();
      toast.success(`Árbol ${
        newStatus === 'approved' ? 'aprobado' : 
        newStatus === 'rejected' ? 'rechazado' : 
        'actualizado'
      } correctamente`);
    } catch (error) {
      console.error('Error updating tree status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const handleDelete = async (tree: Tree) => {
    const isPermanent = tree.status === 'deleted';
    const message = isPermanent 
      ? '¿Estás seguro de eliminar permanentemente este árbol?' 
      : '¿Estás seguro de mover este árbol a la papelera?';

    if (!window.confirm(message)) return;

    try {
      if (isPermanent) {
        await deleteDoc(doc(db, 'trees', tree.id));
        if (tree.imageUrl) {
          const imageRef = ref(storage, tree.imageUrl);
          await deleteObject(imageRef).catch(console.error);
        }
      } else {
        await handleStatusChange(tree, 'deleted');
      }

      await logAdminActivity('delete', {
        treeId: tree.id,
        treeName: tree.name,
        permanent: isPermanent
      });

      await loadTrees();
      toast.success(isPermanent ? 'Árbol eliminado permanentemente' : 'Árbol movido a la papelera');
    } catch (error) {
      console.error('Error deleting tree:', error);
      toast.error('Error al eliminar el árbol');
    }
  };

  const handleRestore = async (tree: Tree) => {
    try {
      await handleStatusChange(tree, 'pending');
      toast.success('Árbol restaurado correctamente');
    } catch (error) {
      console.error('Error restoring tree:', error);
      toast.error('Error al restaurar el árbol');
    }
  };

  const filteredTrees = trees.filter(tree => 
    statusFilter === 'all' ? true : tree.status === statusFilter
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <TreePine className="h-6 w-6 mr-2 text-primary" />
          Participantes ({filteredTrees.length})
        </h2>
        
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobados</option>
            <option value="rejected">Rechazados</option>
            <option value="deleted">Papelera</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="relative">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
            <div className="min-w-[1200px]">
              <div className="grid grid-cols-[80px_1fr_1fr_1.5fr_80px_120px_160px] px-6 py-3">
                <div className="text-xs font-medium text-gray-500 uppercase">Imagen</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Nombre</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Creador</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Email</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Votos</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Estado</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Acciones</div>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="overflow-x-auto">
            <div className="min-w-[1200px]">
              {filteredTrees.map((tree, index) => (
                <div 
                  key={tree.id}
                  className="grid grid-cols-[80px_1fr_1fr_1.5fr_80px_120px_160px] px-6 py-4 items-center hover:bg-gray-50 border-b border-gray-200"
                >
                  <div>
                    <ImageWithFallback 
                      src={tree.imageUrl} 
                      alt={tree.name}
                      className="h-12 w-12 object-cover rounded-lg"
                      width={48}
                      height={48}
                      priority={index < 5}
                    />
                  </div>
                  <div className="truncate">{tree.name}</div>
                  <div className="truncate">{tree.creator}</div>
                  <div className="truncate">{tree.email}</div>
                  <div>{tree.votes}</div>
                  <div>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      tree.status === 'approved' ? 'bg-green-100 text-green-800' :
                      tree.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      tree.status === 'deleted' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {tree.status === 'approved' ? 'Aprobado' :
                       tree.status === 'rejected' ? 'Rechazado' :
                       tree.status === 'deleted' ? 'En papelera' :
                       'Pendiente'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {tree.status === 'deleted' ? (
                      <>
                        <button
                          onClick={() => handleRestore(tree)}
                          className="p-2 text-green-500 hover:bg-green-50 rounded-full"
                          title="Restaurar"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tree)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                          title="Eliminar permanentemente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingTree(tree)}
                          className="p-2 text-blue-500 hover:bg-blue-50 rounded-full"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {tree.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(tree, 'approved')}
                              className="p-2 text-green-500 hover:bg-green-50 rounded-full"
                              title="Aprobar"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleStatusChange(tree, 'rejected')}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                              title="Rechazar"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(tree)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                          title="Mover a papelera"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}

              {filteredTrees.length === 0 && (
                <div className="text-center py-12">
                  <TreePine className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No hay árboles {statusFilter !== 'all' ? `${statusFilter === 'deleted' ? 'en papelera' : statusFilter + 's'}` : ''} para mostrar</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {editingTree && (
        <EditTreeModal
          tree={editingTree}
          onClose={() => setEditingTree(null)}
          onSuccess={() => {
            setEditingTree(null);
            loadTrees();
          }}
        />
      )}
    </div>
  );
}